import React from "react";

type BoundaryProps<P extends object = object> = {
  children: React.ReactNode;
  fallback?: React.ReactNode | ((props: P) => React.ReactNode);
  fallbackProps?: P;
  onError?: (error: Error, info: React.ErrorInfo) => void;
};
type BoundaryState = {
  hasError: boolean;
};
class ReactErrorBoundary<P extends object = object> extends React.Component<BoundaryProps<P>, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info);
  }

  render() {
    if (this.state.hasError) {
      const { fallback, fallbackProps } = this.props;
      if (typeof fallback === "function") {
        return fallbackProps !== undefined ? fallback(fallbackProps) : null;
      }
      return fallback ?? null;
    }
    return this.props.children;
  }
}

type HOCOptions<P extends object> = {
  fallback?: React.ReactNode | ((props: P) => React.ReactNode);
  onError?: (error: Error, info: React.ErrorInfo) => void;
};

export { ReactErrorBoundary };

export const withReactErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  options?: HOCOptions<P>
): React.FC<P> => {
  const Wrapped: React.FC<P> = (props) => (
    <ReactErrorBoundary fallback={options?.fallback} fallbackProps={props} onError={options?.onError}>
      <Component {...props} />
    </ReactErrorBoundary>
  );
  Wrapped.displayName = `withReactErrorBoundary(${Component.displayName || Component.name || "Component"})`;
  return Wrapped;
};
